import React, { useEffect } from "react";
import { LoomPhotoItem } from "./loomPhotos";
import { FullViewImage } from "../../media/FullViewImage";

type Props = {
  items: LoomPhotoItem[];
  index: number;
  onClose: () => void;
  onIndex: (index: number) => void;
};

const GeoPhotoLightbox = ({ items, index, onClose, onIndex }: Props) => {
  const open = index >= 0 && items.length > 0;
  const safeIndex = open ? Math.min(index, items.length - 1) : -1;
  const current = safeIndex >= 0 ? items[safeIndex] : null;

  useEffect(() => {
    if (!current) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      } else if (event.key === "ArrowRight" && items.length > 1) {
        onIndex((safeIndex + 1) % items.length);
      } else if (event.key === "ArrowLeft" && items.length > 1) {
        onIndex((safeIndex - 1 + items.length) % items.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [current, items.length, onClose, onIndex, safeIndex]);

  if (!current) {
    return null;
  }

  return (
    <div className="geo-lightbox" role="dialog" aria-modal="true" aria-label={current.label}>
      <button type="button" className="geo-lightbox__backdrop" onClick={onClose} aria-label="Close photo" />
      <div className="geo-lightbox__stage">
        {items.length > 1 ? (
          <button
            type="button"
            className="geo-lightbox__nav geo-lightbox__nav--prev"
            onClick={() => onIndex((safeIndex - 1 + items.length) % items.length)}
            aria-label="Previous photo"
          >
            ‹
          </button>
        ) : null}
        <FullViewImage item={current.url} src={current.url} alt={current.label} />
        {items.length > 1 ? (
          <button
            type="button"
            className="geo-lightbox__nav geo-lightbox__nav--next"
            onClick={() => onIndex((safeIndex + 1) % items.length)}
            aria-label="Next photo"
          >
            ›
          </button>
        ) : null}
        <button type="button" className="geo-lightbox__close" onClick={onClose}>
          Close
        </button>
        <p className="geo-lightbox__caption">
          {current.label}
          {items.length > 1 ? ` · ${safeIndex + 1} of ${items.length}` : ""}
        </p>
      </div>
    </div>
  );
};

export default GeoPhotoLightbox;
